import React from 'react';
import type { ChatMessage } from '@/types/chat';
import { ERole } from '@/types/chat';
import UserBubble from './UserBubble';
import AssistantCard from './AssistantCard';
import { useChatStore } from '@/store';
import { MagicAIIcon, MagicAIIconBeta, StartIcon } from '@/assets/SVGIcon';
import { common } from '@/common';
import { t } from '@/i18n';
import { isMindSource } from '@/utils/mindHost';
import type { MindAskPayload } from './MindResponseBlocks';
import './index.less';

interface MessageListProps {
  messages: ChatMessage[];
  /** init.source。Mind 时展示知识助理引导词 */
  hostSource?: string;
  onRetry?: (messageId: string) => void;
  onGenerateChart?: (messageId: string, sqlRecordId: string, sqlChatId: string) => Promise<void>;
  onSummarize?: (sqlRecordId: string, sqlChatId: string) => Promise<void>;
  onAnswerAsk?: (messageId: string, questionId: string, payload: MindAskPayload) => Promise<void>;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  hostSource,
  onRetry,
  onGenerateChart,
  onSummarize,
  onAnswerAsk,
}) => {
  const currentRole = useChatStore((s) => s.currentRole);
  const mindSource = isMindSource(hostSource !== undefined ? hostSource : common.source);

  if (!messages.length) {
    return (
      <div className="msg-empty">
        <div className="msg-empty-title">
          <div className="msg-empty__icon">
            {/* <MagicAIIcon /> */}
            <MagicAIIconBeta />
          </div>
          <div className="msg-empty-title__text">
            Magic AI <StartIcon />
          </div>
          <div className="msg-empty__desc">
            {t("empty.beta.desc")}
          </div>
        </div>

        {mindSource ? (
          <p className={`msg-empty__subtitle ${common.language}`}>
            {t('empty.mind.subtitle').split('\n').map((line, i) => (
              <span key={i} className="msg-empty__subtitle-line">{line}</span>
            ))}
          </p>
        ) : null}
        {!mindSource && currentRole?.id === ERole.BUSINESS_ANALYSIS && (
          <p className={`msg-empty__subtitle ${common.language}`}>
            {t('empty.businessAssistant.subtitle').split('\n').map((line, i) => (
              <span key={i} className="msg-empty__subtitle-line">{line}</span>
            ))}
          </p>
        )}
        {!mindSource && currentRole?.id === ERole.ANALYST && (
          <p className={`msg-empty__subtitle ${common.language}`}>
            {t('empty.dataAssistant.subtitle').split('\n').map((line, i) => (
              <span key={i} className="msg-empty__subtitle-line">{line}</span>
            ))}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="msg-list">
      {messages.map((msg) =>
        msg.role === 'user' ? (
          <UserBubble key={msg.id} message={msg} />
        ) : (
          <AssistantCard
            key={msg.id}
            message={msg}
            onRetry={onRetry}
            onGenerateChart={onGenerateChart}
            onSummarize={onSummarize}
            showCopy={mindSource}
            onAnswerAsk={onAnswerAsk
              ? (questionId, payload) => onAnswerAsk(msg.id, questionId, payload)
              : undefined}
          />
        ),
      )}
    </div>
  );
};

export default MessageList;
